import api from '.';
import {
	BulkCreateMarketsDto,
	BulkDeleteMarketsDto,
	BulkSettleMarketsDto,
	BulkShiftMarketTimeDto,
	BulkUnsettleMarketsDto,
	BulkUpdateMarketCategoryDto,
	BulkUpdateMarketContextDto,
	BulkUpdateOddsDto,
	CloseMarketDto,
	CreateMarketDto,
	GenerateEspnMarketsDto,
	GenerateFuturesMarketsDto,
	GenerateNewMarketsDto,
	MarketTimeField,
	PushNextMarketToLiveDto,
	ReorderMarketPriorityDto,
	SettleMarketDto,
	UnsettleMarketDto,
	VoidMarketDto,
} from '@sportspredict/shared';

export { MarketTimeField };

export interface BulkCreateMarketsRequest {
	event_id: string;
	data: Partial<CreateMarketDto>[];
	match_id?: string | null;
	market_open_delay?: number;
}

export const marketManageApi = api.injectEndpoints({
	endpoints: (builder) => ({
		settleMarket: builder.mutation<unknown, SettleMarketDto>({
			query: (data) => ({ url: 'market-manage/settle', method: 'POST', body: data }),
			invalidatesTags: ['Market'],
		}),
		unsettleMarket: builder.mutation<unknown, UnsettleMarketDto>({
			query: (data) => ({ url: 'market-manage/unsettle', method: 'POST', body: data }),
			invalidatesTags: ['Market'],
		}),
		closeMarket: builder.mutation<unknown, CloseMarketDto>({
			query: (data) => ({ url: 'market-manage/close', method: 'POST', body: data }),
			invalidatesTags: ['Market'],
		}),
		voidMarket: builder.mutation<unknown, VoidMarketDto>({
			query: (data) => ({ url: 'market-manage/void', method: 'POST', body: data }),
			invalidatesTags: ['Market'],
		}),
		reorderMarketPriority: builder.mutation<unknown, ReorderMarketPriorityDto>({
			query: (data) => ({ url: 'market-manage/priority', method: 'PATCH', body: data }),
			invalidatesTags: ['Market'],
		}),
		reorderH2HMarketPriority: builder.mutation<unknown, ReorderMarketPriorityDto>({
			query: (data) => ({ url: 'market-manage/h2h-priority', method: 'PATCH', body: data }),
			invalidatesTags: ['Market'],
		}),
		pushNextMarketToLive: builder.mutation<unknown, PushNextMarketToLiveDto>({
			query: (data) => ({ url: 'market-manage/live', method: 'POST', body: data }),
			invalidatesTags: ['Market'],
		}),
		generateNewMarkets: builder.mutation<unknown, GenerateNewMarketsDto>({
			query: (data) => ({
				url: 'market-manage/generate-new-markets',
				method: 'POST',
				body: data,
			}),
		}),
		generateFuturesMarkets: builder.mutation<unknown, GenerateFuturesMarketsDto>({
			query: (data) => ({
				url: 'market-manage/generate-futures-markets',
				method: 'POST',
				body: data,
			}),
		}),
		generateEspnMarkets: builder.mutation<unknown, GenerateEspnMarketsDto>({
			query: (data) => ({
				url: 'market-manage/generate-espn-markets',
				method: 'POST',
				body: data,
			}),
		}),
		bulkSettleMarkets: builder.mutation<unknown, BulkSettleMarketsDto>({
			query: (data) => ({ url: 'market-manage/bulk-settle', method: 'POST', body: data }),
			invalidatesTags: ['Market'],
		}),
		bulkUnsettleMarkets: builder.mutation<unknown, BulkUnsettleMarketsDto>({
			query: (data) => ({ url: 'market-manage/bulk-unsettle', method: 'POST', body: data }),
			invalidatesTags: ['Market'],
		}),
		bulkShiftMarketTime: builder.mutation<unknown, BulkShiftMarketTimeDto>({
			query: (data) => ({ url: 'market-manage/bulk-shift-time', method: 'POST', body: data }),
			invalidatesTags: ['Market'],
		}),
		bulkDeleteMarkets: builder.mutation<unknown, BulkDeleteMarketsDto>({
			query: (data) => ({ url: 'market-manage/bulk-delete', method: 'POST', body: data }),
			invalidatesTags: ['Market'],
		}),
		bulkUpdateOdds: builder.mutation<unknown, BulkUpdateOddsDto>({
			query: (data) => ({ url: 'market-manage/bulk-update-odds', method: 'POST', body: data }),
			invalidatesTags: ['Market'],
		}),
		bulkUpdateMarketCategory: builder.mutation<unknown, BulkUpdateMarketCategoryDto>({
			query: (data) => ({ url: 'market-manage/bulk-update-category', method: 'POST', body: data }),
			invalidatesTags: ['Market'],
		}),
		bulkUpdateMarketContext: builder.mutation<unknown, BulkUpdateMarketContextDto>({
			query: (data) => ({ url: 'market-manage/bulk-update-context', method: 'POST', body: data }),
			invalidatesTags: ['Market'],
		}),
		bulkCreateMarkets: builder.mutation<unknown, BulkCreateMarketsDto>({
			query: (data) => ({ url: 'market-manage/bulk', method: 'POST', body: data }),
			invalidatesTags: ['Market'],
		}),
	}),
	overrideExisting: false,
});

export const {
	useSettleMarketMutation,
	useUnsettleMarketMutation,
	useCloseMarketMutation,
	useVoidMarketMutation,
	useReorderMarketPriorityMutation,
	useReorderH2HMarketPriorityMutation,
	usePushNextMarketToLiveMutation,
	useGenerateNewMarketsMutation,
	useGenerateFuturesMarketsMutation,
	useGenerateEspnMarketsMutation,
	useBulkSettleMarketsMutation,
	useBulkUnsettleMarketsMutation,
	useBulkShiftMarketTimeMutation,
	useBulkDeleteMarketsMutation,
	useBulkUpdateOddsMutation,
	useBulkUpdateMarketCategoryMutation,
	useBulkUpdateMarketContextMutation,
	useBulkCreateMarketsMutation,
} = marketManageApi;

export default marketManageApi;
